/**
 * Recording a whole visit (30–60 min), in parts.
 *
 * The chat mic's VoiceRecorder is live dictation (expo-speech-recognition, a
 * WAV sent as a base64 data URL), bounded by the 6 MB request body. A visit
 * needs something else:
 *
 *   - Mono, low bitrate (services/visitAudioEngine: expo-audio on a phone,
 *     MediaRecorder in a browser or the desktop app).
 *   - A new part every 10 minutes: stop, keep the file, start again. Each part
 *     is complete on its own, so the server can transcribe it alone, and a
 *     crash or a kill loses at most the open part. The cost is a gap of a
 *     fraction of a second at each change.
 *   - Each finished part uploads at once through the ordinary presigned path
 *     (uploadDocument), as a hidden `visit_recording_segment` that only the
 *     recorder can see; the server transcribes it and deletes its audio.
 *   - On a phone, a manifest in AsyncStorage (per account and member) so a
 *     recording the app never finished sending is offered again next launch.
 *   - The screen stays on while recording (expo-keep-awake with its own tag,
 *     released on pause, stop, discard and error).
 *
 * Nothing records until start is called from the consent sheet's button.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { activateKeepAwakeAsync, deactivateKeepAwake } from 'expo-keep-awake';
import { store } from '../store';
import { uploadDocument } from '../store/actions/notes';
import { discardVisitRecordingOnServer, finalizeVisitRecording, startVisitRecordingOnServer } from '../utilities/visitRecordingApi';
import debug from '../utilities/debug';
import { engine } from './visitAudioEngine';
import { claimRecordingLock, recordingHeldByOther } from './recordingLock';

export const SEGMENT_MS = 10 * 60 * 1000;
export const KEEP_AWAKE_TAG = 'visit-recording';
const MANIFEST_KEY = '@eleplan/visit_recording_manifest';
/** Refuse to start below this: an hour is ~14 MB, and the OS needs room too. */
const MIN_FREE_BYTES = 200 * 1024 * 1024;
const LEVEL_POLL_MS = 200;
/** A recording stops by itself at this length. The server takes up to 40 ten-minute parts. */
export const MAX_RECORDING_MS = 3 * 60 * 60 * 1000;
/**
 * After the phone leaves the place of the appointment being recorded, the
 * recording stops this much later unless it comes back first: the lab down
 * the hall, the car park, a location fix that drifts.
 */
export const LEAVE_GRACE_MS = 5 * 60 * 1000;

export type VisitRecorderStatus = 'idle' | 'starting' | 'recording' | 'paused' | 'stopping' | 'error';

export interface VisitRecordingSegment { index: number; uri: string; mime: string; ext: string; uploaded: boolean }

export interface VisitRecordingManifest {
  /** This phone's id for the recording, for local files only. */
  recordingId: string;
  /** The server's id (POST visit-recording start), asked for before the first part is sent. */
  serverId?: string | null;
  projectId: number;
  eventId: string | null;
  label: string | null;
  conversationId: string | null;
  startedAt: string;
  durationMs: number;
  segments: VisitRecordingSegment[];
  stopped: boolean;
}

export interface VisitRecorderState {
  status: VisitRecorderStatus;
  recordingId: string | null;
  projectId: number | null;
  label: string | null;
  /** The calendar event of the appointment being recorded, when there is one. */
  eventId: string | null;
  elapsedMs: number;
  /** 0..1, for the waveform. */
  level: number;
  segments: number;
  /** null until the first start: whether recording continues with the screen locked. */
  backgroundSupported: boolean | null;
  /** Set when the OS paused the recording (a call, Siri). */
  interrupted: boolean;
  error: string | null;
}

const INITIAL: VisitRecorderState = {
  status: 'idle', recordingId: null, projectId: null, label: null, eventId: null, elapsedMs: 0, level: 0,
  segments: 0, backgroundSupported: null, interrupted: false, error: null,
};

let state: VisitRecorderState = { ...INITIAL };
let manifest: VisitRecordingManifest | null = null;
/** The account and member the recording belongs to, fixed when it starts. */
let ownerKey: string | null = null;
/**
 * Bumped by suspendVisitRecording (sign-out, account switch). A start or a
 * stop that was in flight re-checks it after every await and gives up rather
 * than open the microphone, or send anything, under the next session.
 */
let sessionGeneration = 0;
/**
 * Bumped by every start and by cancelVisitRecordingStart: only the newest
 * start may open the microphone. Separate from sessionGeneration, which also
 * stops sends in flight; cancelling a start must not touch those.
 */
let startGeneration = 0;
/** The storage key of each manifest, from the session that made (or loaded) it. */
const keyOf = new WeakMap<VisitRecordingManifest, string>();
/** Whether a recording panel for the recording's plan is on screen (the global indicator hides then). */
let panelProjectId: number | null = null;
const panelListeners = new Set<() => void>();
let segmentStartedAt = 0;
let accumulatedMs = 0;
let rotateTimer: ReturnType<typeof setTimeout> | null = null;
let levelTimer: ReturnType<typeof setInterval> | null = null;
let rotating = false;
let rotationTask: Promise<void> | null = null;
let resumeTask: Promise<void> | null = null;
let captureGeneration = 0;
let captureQueue: Promise<void> = Promise.resolve();
/** When the phone left the appointment's place (a geofence exit), until it stops or comes back. */
let leftAt: number | null = null;

// Serialize microphone opens/resumes/releases; closePart has already detached
// its recorder, and may finish saving that old part after sign-out.
function captureOperation<T>(operation: () => Promise<T>): Promise<T> {
  const result = captureQueue.then(operation);
  captureQueue = result.then(() => {}, () => {});
  return result;
}
const listeners = new Set<(s: VisitRecorderState) => void>();

function emit(patch: Partial<VisitRecorderState>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => { try { l(state); } catch { /* a listener's own problem */ } });
}

export function getVisitRecorderState(): VisitRecorderState { return state; }

export function subscribeVisitRecorder(listener: (s: VisitRecorderState) => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function isVisitRecordingSupported(): boolean {
  return engine.supported();
}

/** The chat says its panel for a plan is showing (or gone), so the global indicator can step aside. */
export function setVisitPanelProject(projectId: number | null) {
  panelProjectId = projectId;
  panelListeners.forEach((l) => { try { l(); } catch { /* */ } });
}
export function getVisitPanelProject(): number | null { return panelProjectId; }
export function subscribeVisitPanel(listener: () => void): () => void {
  panelListeners.add(listener);
  return () => { panelListeners.delete(listener); };
}

function manifestKey(): string | null {
  const s: any = store.getState();
  const accountId = s?.session?.account?.account_id;
  const cognitoId = s?.authentication?.user?.cognito_id;
  return accountId && cognitoId ? `${MANIFEST_KEY}:${encodeURIComponent(accountId)}:${encodeURIComponent(cognitoId)}` : null;
}

async function saveManifest(m: VisitRecordingManifest | null = manifest, explicitKey?: string | null) {
  // A browser's parts are in-memory blobs: nothing to recover after a reload.
  if (!engine.persistsParts) return;
  // Saved under the member it belongs to, even after they signed out.
  const key = explicitKey ?? (m ? keyOf.get(m) : null) ?? ownerKey;
  if (!key) return;
  if (m) await AsyncStorage.setItem(key, JSON.stringify(m)).catch(() => {});
  else await AsyncStorage.removeItem(key).catch(() => {});
}

/** A recording this member stopped (or the app lost) but never finished sending, for this plan. */
export async function loadUnfinishedRecording(projectId: number): Promise<VisitRecordingManifest | null> {
  if (!engine.persistsParts || (state.status !== 'idle' && state.status !== 'error')) return null;
  const key = manifestKey();
  if (!key) return null;
  try {
    const raw = await AsyncStorage.getItem(key);
    const saved = raw ? JSON.parse(raw) as VisitRecordingManifest : null;
    if (!saved || Number(saved.projectId) !== Number(projectId) || !saved.segments?.length) return null;
    keyOf.set(saved, key);
    return saved;
  } catch {
    return null;
  }
}

function newRecordingId(): string {
  const webCrypto = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (webCrypto?.randomUUID) return webCrypto.randomUUID();
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return require('expo-crypto').randomUUID();
  } catch { /* fall through */ }
  return `rec-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function stopTimers() {
  if (rotateTimer) clearTimeout(rotateTimer);
  if (levelTimer) clearInterval(levelTimer);
  rotateTimer = null;
  levelTimer = null;
}

const keepAwake = (on: boolean) => (on
  ? activateKeepAwakeAsync(KEEP_AWAKE_TAG)
  : deactivateKeepAwake(KEEP_AWAKE_TAG)).catch(() => {});

function startTimers() {
  stopTimers();
  const remaining = Math.max(1000, SEGMENT_MS - (Date.now() - segmentStartedAt));
  rotateTimer = setTimeout(() => {
    const task = rotateSegment().catch(() => {});
    rotationTask = task;
    task.finally(() => { if (rotationTask === task) rotationTask = null; });
  }, remaining);
  levelTimer = setInterval(() => {
    if (state.status !== 'recording' || rotating) return;
    // The OS paused it (a phone call, Siri): show it, and let them resume.
    if (!engine.isRecording()) {
      accumulatedMs += Date.now() - segmentStartedAt;
      segmentStartedAt = Date.now();
      stopTimers();
      keepAwake(false);
      emit({ status: 'paused', interrupted: true, level: 0, elapsedMs: accumulatedMs });
      return;
    }
    const elapsedMs = accumulatedMs + (Date.now() - segmentStartedAt);
    if (elapsedMs >= MAX_RECORDING_MS) { autoStop('cap'); return; }
    if (leftAt != null && Date.now() - leftAt >= LEAVE_GRACE_MS) { autoStop('left'); return; }
    emit({ level: engine.level(), elapsedMs });
  }, LEVEL_POLL_MS);
}

/** Close the open part, keep its file, and start sending it. */
async function closeSegment(): Promise<void> {
  // Held here: a sign-out while the part is being closed clears the module's copy.
  const m = manifest;
  if (!m) return;
  const generation = sessionGeneration;
  const index = m.segments.length;
  let part = null;
  try {
    part = await engine.closePart(engine.partsDir(m.recordingId), index);
  } catch (err: any) {
    debug.warn('[visitRecorder] could not keep part', { error: err?.message });
  }
  if (!part) return;
  m.segments.push({ index, ...part, uploaded: false });
  await saveManifest(m);
  if (generation !== sessionGeneration) return;
  emit({ segments: m.segments.length });
  uploadPendingSegments(m).catch(() => {});
}

async function rotateSegment() {
  if (state.status !== 'recording' || rotating || !manifest) return;
  const m = manifest;
  const generation = sessionGeneration;
  const capture = captureGeneration;
  const current = () => generation === sessionGeneration && capture === captureGeneration &&
    manifest === m && state.status === 'recording';
  rotating = true;
  try {
    accumulatedMs += Date.now() - segmentStartedAt;
    segmentStartedAt = Date.now();
    await closeSegment();
    if (!current()) return;
    const opened = await captureOperation(async () => {
      if (!current()) return false;
      try { await engine.startPart(); }
      catch (err) { await engine.release(); throw err; }
      if (!current()) {
        if (generation === sessionGeneration && manifest === m && state.status === 'paused') engine.pause();
        else await engine.release();
        return false;
      }
      return true;
    });
    if (!opened || !current()) return;
    segmentStartedAt = Date.now();
    startTimers();
  } catch (err: any) {
    if (!current()) return;
    debug.warn('[visitRecorder] could not start the next part', { error: err?.message });
    await failRecording('The recording stopped. What was recorded so far is kept.');
  } finally {
    rotating = false;
  }
}

async function failRecording(message: string) {
  stopTimers();
  keepAwake(false);
  if (manifest) {
    manifest.durationMs = accumulatedMs;
    manifest.stopped = true;
    await saveManifest();
  }
  emit({ status: 'error', error: message, level: 0 });
}

// The visit recorder's place in the app-wide one-recording lock: busy from
// "starting" until it is idle again.
const LOCK_OWNER = {};
const visitBusy = () => state.status === 'starting' || state.status === 'recording' || state.status === 'paused' || state.status === 'stopping';

export async function startVisitRecording(args: {
  projectId: number;
  eventId?: string | null;
  label?: string | null;
  conversationId?: string | null;
}): Promise<{ ok: boolean; error?: string }> {
  if (!isVisitRecordingSupported()) return { ok: false, error: 'This device can’t record here. Open the Eleplan app on your phone.' };
  if (state.status === 'recording' || state.status === 'paused' || state.status === 'starting' || state.status === 'stopping') {
    return { ok: false, error: 'A visit is already being recorded.' };
  }
  // A voice note or dictation has the microphone.
  if (recordingHeldByOther(LOCK_OWNER)) {
    emit({ ...INITIAL, status: 'error', projectId: args.projectId, label: args.label ?? null, error: 'Another recording is going. Stop it first.', backgroundSupported: engine.backgroundSupported() });
    return { ok: false, error: 'busy' };
  }
  claimRecordingLock(LOCK_OWNER, visitBusy);
  // Ask again each time: a store build may have arrived since the last refusal.
  engine.resetBackgroundProbe?.();
  // The member this recording belongs to, fixed now: a sign-out or an account
  // switch while the permission prompt is up must not hand it to someone else.
  const generation = sessionGeneration;
  const startKey = manifestKey();
  // No member loaded yet (say, a cold start from a recording link): say so,
  // rather than leave the panel on "Starting…" with nothing behind it.
  if (!startKey) {
    emit({
      ...INITIAL, status: 'error', projectId: args.projectId, label: args.label ?? null,
      error: 'The recording couldn’t start yet. Try again in a moment.', backgroundSupported: engine.backgroundSupported(),
    });
    return { ok: false, error: 'no-member' };
  }
  const ticket = ++startGeneration;
  const abandoned = () => generation !== sessionGeneration || ticket !== startGeneration;
  let mine: VisitRecordingManifest | null = null;
  // Signed out (suspendVisitRecording) or cancelled (cancelVisitRecordingStart):
  // drop this start's manifest, and release the microphone it may have opened
  // (a browser opens it to ask), unless a newer start holds the recorder now.
  const giveUp = async () => {
    if (mine && manifest === mine) {
      manifest = null;
      await saveManifest(null, startKey);
    }
    await captureOperation(async () => {
      if (state.status === 'idle' || state.status === 'error') await engine.release();
    });
    return { ok: false, error: generation !== sessionGeneration ? 'signed-out' : 'cancelled' };
  };
  leftAt = null;
  emit({ ...INITIAL, status: 'starting', projectId: args.projectId, label: args.label ?? null, eventId: args.eventId ?? null, backgroundSupported: engine.backgroundSupported() });
  try {
    // Permission prompts may never settle. Only actual capture operations
    // are serialized; abandoned prompts cannot block another session.
    await Promise.all([rotationTask, resumeTask, captureQueue]);
    if (abandoned()) return giveUp();
    const allowed = await engine.requestPermission(() => !abandoned());
    if (abandoned()) return giveUp();
    if (!allowed) {
      emit({ status: 'error', error: 'Eleplan needs the microphone to record. You can allow it in Settings.' });
      return { ok: false, error: 'not-allowed' };
    }
    const free = await engine.freeBytes();
    if (abandoned()) return giveUp();
    if (free < MIN_FREE_BYTES) {
      emit({ status: 'error', error: 'This device is almost out of space. Free up some room, then try again.' });
      return { ok: false, error: 'low-storage' };
    }
    ownerKey = startKey;
    mine = {
      recordingId: newRecordingId(),
      serverId: null,
      projectId: args.projectId,
      eventId: args.eventId ?? null,
      label: args.label ?? null,
      conversationId: args.conversationId ?? null,
      startedAt: new Date().toISOString(),
      durationMs: 0,
      segments: [],
      stopped: false,
    };
    manifest = mine;
    keyOf.set(mine, startKey);
    await saveManifest(mine);
    if (abandoned()) return giveUp();
    await captureOperation(async () => {
      if (abandoned()) return;
      try { await engine.startPart(); }
      catch (err) { await engine.release(); throw err; }
      if (abandoned()) await engine.release();
    });
    // Signed out or cancelled while the microphone was opening: close it at once.
    if (abandoned()) return giveUp();
    accumulatedMs = 0;
    segmentStartedAt = Date.now();
    keepAwake(true);
    startTimers();
    emit({ status: 'recording', recordingId: mine.recordingId, backgroundSupported: engine.backgroundSupported(), error: null });
    debug.log('voice', '[visitRecorder] recording started', { background: engine.backgroundSupported() });
    return { ok: true };
  } catch (err: any) {
    debug.warn('[visitRecorder] could not start', { error: err?.message });
    if (abandoned()) return giveUp();
    manifest = null;
    await saveManifest(null, startKey);
    await captureOperation(() => engine.release());
    emit({ status: 'error', error: 'The microphone could not start. Try again.' });
    return { ok: false, error: 'capture_error' };
  }
}

/**
 * Give up a start whose microphone hasn't opened yet: the person closed the
 * consent sheet, perhaps because the microphone never answered. The recorder
 * is idle at once, so a new start can begin; the start in flight notices after
 * its next await and releases what it opened. Nothing was recorded.
 */
export function cancelVisitRecordingStart(): void {
  if (state.status !== 'starting') return;
  startGeneration += 1;
  emit({ ...INITIAL, backgroundSupported: engine.backgroundSupported() });
}

export function pauseVisitRecording() {
  if (state.status !== 'recording' && !resumeTask) return;
  captureGeneration += 1;
  engine.pause();
  if (state.status === 'recording') accumulatedMs += Date.now() - segmentStartedAt;
  segmentStartedAt = Date.now();
  stopTimers();
  keepAwake(false);
  emit({ status: 'paused', level: 0, elapsedMs: accumulatedMs });
}

export function resumeVisitRecording(): Promise<void> {
  if (resumeTask) return resumeTask;
  const task = resumeRecording();
  resumeTask = task;
  task.finally(() => { if (resumeTask === task) resumeTask = null; }).catch(() => {});
  return task;
}

async function resumeRecording(): Promise<void> {
  if (state.status !== 'paused' || !manifest) return;
  const m = manifest;
  const generation = sessionGeneration;
  const capture = captureGeneration;
  const current = () => generation === sessionGeneration && capture === captureGeneration &&
    manifest === m && state.status === 'paused';
  try {
    await rotationTask;
    if (!current()) return;
    const resumed = await captureOperation(async () => {
      if (!current()) return false;
      try { await engine.resume(); }
      catch (err) {
        // release discards the native recorder: persist its paused part first.
        await closeSegment();
        await engine.release();
        throw err;
      }
      if (!current()) {
        if (generation === sessionGeneration && manifest === m && state.status === 'paused') engine.pause();
        else await engine.release();
        return false;
      }
      return true;
    });
    if (!resumed || !current()) return;
    // Resumed by hand: they mean to keep recording, wherever they are now.
    leftAt = null;
    segmentStartedAt = Date.now();
    keepAwake(true);
    startTimers();
    emit({ status: 'recording', interrupted: false });
  } catch (err: any) {
    if (!current()) return;
    debug.warn('[visitRecorder] could not resume', { error: err?.message });
    await failRecording('The recording could not resume. What was recorded so far is kept.');
  }
}

/**
 * "Stop and summarize": close the last part, send whatever is not sent yet,
 * then tell the server how many parts make up the visit.
 */
export async function stopVisitRecording(): Promise<{ ok: boolean; recordingId: string | null; error?: string }> {
  if (!manifest || (state.status !== 'recording' && state.status !== 'paused' && state.status !== 'error')) {
    return { ok: false, recordingId: null };
  }
  // The recording and its session, held here: a sign-out during the send
  // (suspendVisitRecording) clears the module's copy, not this one.
  const m = manifest;
  const generation = sessionGeneration;
  const wasRecording = state.status === 'recording';
  captureGeneration += 1;
  emit({ status: 'stopping', level: 0 });
  stopTimers();
  if (wasRecording) accumulatedMs += Date.now() - segmentStartedAt;
  await Promise.all([rotationTask, resumeTask]);
  await closeSegment();
  keepAwake(false);
  if (generation === sessionGeneration) await captureOperation(() => engine.release());
  m.durationMs = accumulatedMs;
  m.stopped = true;
  await saveManifest(m);
  const result = generation === sessionGeneration
    ? await finishRecording(m)
    : { ok: false, recordingId: m.serverId || m.recordingId, error: 'Signed out before the recording was sent.' };
  if (generation === sessionGeneration) emit({ ...INITIAL, backgroundSupported: engine.backgroundSupported() });
  return result;
}

// ── Stopping by itself ─────────────────────────────────────────────────────

export type AutoStopReason = 'left' | 'cap';
export interface AutoStop {
  reason: AutoStopReason;
  projectId: number;
  recordingId: string;
  /** The same stop as "Stop and summarize", so the chat's card can follow it. */
  done: Promise<{ ok: boolean; recordingId: string | null; error?: string }>;
}
const autoStopListeners = new Set<(stop: AutoStop) => void>();

export function onVisitAutoStop(listener: (stop: AutoStop) => void): () => void {
  autoStopListeners.add(listener);
  return () => { autoStopListeners.delete(listener); };
}

const sameAppointment = (projectId: number | string, eventId: string) =>
  !!manifest?.eventId && manifest.eventId === eventId && String(manifest.projectId) === String(projectId);

/** The phone left the place of the appointment being recorded (a geofence exit). */
export function noteLeftAppointment(projectId: number | string, eventId: string): void {
  if (state.status !== 'recording' && state.status !== 'paused') return;
  if (!sameAppointment(projectId, eventId) || leftAt != null) return;
  leftAt = Date.now();
  debug.log('voice', '[visitRecorder] left the appointment', { stopsInMs: LEAVE_GRACE_MS });
}

/** Back at the appointment's place before the recording stopped: keep going. */
export function noteBackAtAppointment(projectId: number | string, eventId: string): void {
  if (sameAppointment(projectId, eventId)) leftAt = null;
}

const AUTO_STOP_WHY: Record<AutoStopReason, string> = {
  left: 'You left the appointment, so the recording stopped.',
  cap: `It reached ${MAX_RECORDING_MS / 3_600_000} hours, so it stopped.`,
};

/** A notice that names why it stopped; a tap opens the plan chat on the summary. */
async function notifyAutoStop(reason: AutoStopReason, projectId: number, result: { ok: boolean; recordingId: string | null }) {
  if (Platform.OS === 'web') return;
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const Notifications = require('expo-notifications');
  await Notifications.scheduleNotificationAsync({
    content: {
      title: 'Visit recording stopped',
      body: `${AUTO_STOP_WHY[reason]} ${result.ok ? 'The summary is on its way.' : 'Open the chat to send it.'}`,
      data: {
        category: 'visit_summary_ready',
        project_id: projectId,
        ...(result.ok && result.recordingId ? { recording_id: result.recordingId } : {}),
      },
    },
    trigger: null,
  });
}

function autoStop(reason: AutoStopReason) {
  const { projectId, recordingId } = state;
  if (projectId == null || !recordingId) return;
  leftAt = null;
  debug.log('voice', '[visitRecorder] stopping by itself', { reason });
  // Stops the timers and shows "Saving…" before its first await.
  const done = stopVisitRecording();
  autoStopListeners.forEach((listener) => {
    try { listener({ reason, projectId, recordingId, done }); } catch { /* a listener's own problem */ }
  });
  done.then((result) => notifyAutoStop(reason, projectId, result))
    .catch((err: any) => debug.warn('[visitRecorder] auto-stop notice failed', { error: err?.message }));
}

const partFilename = (serverId: string, s: VisitRecordingSegment) =>
  `visit-${serverId}-${String(s.index).padStart(3, '0')}.${s.ext}`;

/** The server's id for this recording, asked for once (a part can only be sent into it). */
async function ensureServerId(m: VisitRecordingManifest): Promise<string | null> {
  if (m.serverId) return m.serverId;
  const res = await startVisitRecordingOnServer(m.projectId, {
    event_id: m.eventId,
    conversation_id: m.conversationId,
    started_at: m.startedAt,
    time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  if (!res.ok || !res.payload?.recording_id) return null;
  m.serverId = res.payload.recording_id;
  await saveManifest(m);
  return m.serverId;
}

// One upload run at a time: a part closed at a 10-minute boundary may still
// be sending when Stop asks for everything to be sent, and the same part must
// not go up twice.
let uploadQueue: Promise<unknown> = Promise.resolve();

/** Send every part not yet sent, in order; a part's local file goes once it is on the server. */
function uploadPendingSegments(m: VisitRecordingManifest): Promise<boolean> {
  // The session this send was asked for in: a queued send that only starts
  // after a sign-out must not run under the next session.
  const generation = sessionGeneration;
  const run = uploadQueue.then(() => uploadPendingNow(m, generation), () => uploadPendingNow(m, generation));
  uploadQueue = run.catch(() => {});
  return run;
}

async function uploadPendingNow(m: VisitRecordingManifest, generation: number): Promise<boolean> {
  if (generation !== sessionGeneration) return false;
  if (!m.segments.some((s) => !s.uploaded)) return true;
  const serverId = await ensureServerId(m);
  if (!serverId || generation !== sessionGeneration) return false;
  for (const segment of m.segments) {
    // Signed out mid-send: stop. What's left stays in the manifest for its member.
    if (generation !== sessionGeneration) return false;
    if (segment.uploaded) continue;
    const filename = partFilename(serverId, segment);
    const res: any = await new Promise((resolve) => {
      const upload = store.dispatch(uploadDocument(m.projectId, {
        filename,
        name: filename,
        document_type: 'visit_recording_segment',
        mime: segment.mime,
        type: segment.ext,
      }, segment.uri, segment.mime, { onConflict: () => true }) as any) as unknown as Promise<any>;
      upload.then(resolve, () => resolve(null));
    });
    if (!res?.success) return false;
    segment.uploaded = true;
    await saveManifest(m);
    await engine.deleteFile(segment.uri);
  }
  return true;
}

/** Send the rest and finalize. Used at stop, and for a recording the app didn't finish last time. */
export async function finishRecording(m: VisitRecordingManifest): Promise<{ ok: boolean; recordingId: string; error?: string }> {
  const generation = sessionGeneration;
  if (!(await uploadPendingSegments(m)) || generation !== sessionGeneration) {
    return { ok: false, recordingId: m.serverId || m.recordingId, error: 'Part of the recording hasn’t uploaded yet. Check your connection and try again.' };
  }
  if (!m.segments.length) {
    await discardRecording(m);
    return { ok: false, recordingId: m.serverId || m.recordingId, error: 'Nothing was recorded.' };
  }
  const res = await finalizeVisitRecording(m.projectId, {
    recording_id: m.serverId!,
    segment_count: m.segments.length,
    duration_seconds: Math.round(m.durationMs / 1000),
  });
  if (!res.ok) return { ok: false, recordingId: m.serverId || m.recordingId, error: res.message || 'Could not finish the recording.' };
  if (manifest?.recordingId === m.recordingId) manifest = null;
  await saveManifest(null, keyOf.get(m) ?? null);
  if (ownerKey === keyOf.get(m)) ownerKey = null;
  if (engine.partsDir(m.recordingId)) await engine.deleteFile(engine.partsDir(m.recordingId));
  return { ok: true, recordingId: m.serverId! };
}

/**
 * Throw away a recording: stop the microphone, delete local parts and the
 * manifest, and ask the server to delete what was already sent (the parts'
 * rows and every version of their audio). A server discard that fails is left
 * to the server's sweep, which removes an abandoned recording after 48 h.
 */
export async function discardRecording(m: VisitRecordingManifest | null = manifest): Promise<void> {
  captureGeneration += 1;
  sessionGeneration += 1;
  const generation = sessionGeneration;
  stopTimers();
  keepAwake(false);
  await Promise.all([rotationTask, resumeTask]);
  await captureOperation(() => engine.release());
  if (m) {
    for (const s of m.segments) await engine.deleteFile(s.uri);
    if (engine.partsDir(m.recordingId)) await engine.deleteFile(engine.partsDir(m.recordingId));
    if (m.serverId) await discardVisitRecordingOnServer(m.projectId, m.serverId).catch(() => null);
  }
  if (!m || manifest?.recordingId === m.recordingId) manifest = null;
  await saveManifest(null, (m && keyOf.get(m)) ?? ownerKey);
  ownerKey = null;
  if (generation === sessionGeneration) emit({ ...INITIAL, backgroundSupported: engine.backgroundSupported() });
}

/**
 * Sign-out or an account switch while recording: stop the microphone, keep
 * what was recorded as an unfinished recording for the member it belongs to
 * (saved under their key), and clear the recorder. Nothing is sent or deleted.
 */
export async function suspendVisitRecording(): Promise<void> {
  sessionGeneration += 1;
  captureGeneration += 1;
  // Opening the microphone: startVisitRecording sees the change after its
  // next await and closes everything; nothing was recorded yet.
  if (state.status === 'starting') {
    emit({ ...INITIAL, backgroundSupported: engine.backgroundSupported() });
    return;
  }
  // Sending after Stop: the parts are already saved under their member;
  // the send sees the change and stops, and that member can finish it later.
  if (state.status === 'stopping') {
    manifest = null;
    ownerKey = null;
    keepAwake(false);
    await captureOperation(() => engine.release());
    emit({ ...INITIAL, backgroundSupported: engine.backgroundSupported() });
    return;
  }
  if (!manifest || (state.status !== 'recording' && state.status !== 'paused' && state.status !== 'error')) return;
  const wasRecording = state.status === 'recording';
  stopTimers();
  if (wasRecording) accumulatedMs += Date.now() - segmentStartedAt;
  const m = manifest;
  const index = m.segments.length;
  const part = await engine.closePart(engine.partsDir(m.recordingId), index).catch(() => null);
  if (part) m.segments.push({ index, ...part, uploaded: false });
  keepAwake(false);
  await captureOperation(() => engine.release());
  m.durationMs = accumulatedMs;
  m.stopped = true;
  await saveManifest(m);
  manifest = null;
  ownerKey = null;
  emit({ ...INITIAL, backgroundSupported: engine.backgroundSupported() });
}
